import React, { useEffect, useMemo, useState } from "react";
import {
  X, MapPin, AlertTriangle, ExternalLink, Ban, CheckCircle2,
  ChevronRight, ChevronLeft, Loader2, Pencil, MessageCircle, AlertCircle, Trash2, Star,
} from "lucide-react";
import { toast } from "sonner";
import { StatusPill } from "./StatusPill";
import { facilitiesApi, auth, watchlistApi } from "../../lib/api";
import { shareOnWhatsApp } from "../../lib/whatsapp";
import { TID } from "../../constants/testIds";
import { getApiError } from "../../lib/utils";
import { ViewMode } from "./facility-drawer/ViewMode";
import { EditMode } from "./facility-drawer/EditMode";
import { StatusModal } from "./facility-drawer/StatusModal";
import { ReportIssueModal } from "./facility-drawer/ReportIssueModal";
import { DeleteFacilityModal } from "./facility-drawer/DeleteFacilityModal";

/** Slide-in drawer that orchestrates ViewMode / EditMode / status & delete modals
 *  for a single facility. Sub-components live in `./facility-drawer/`. */
export const FacilityDrawer = ({ facilityId, onClose, onChanged }) => {
  const [stack, setStack] = useState([facilityId]);
  const currentId = stack[stack.length - 1];
  const [facility, setFacility] = useState(null);
  const [alts, setAlts] = useState([]);
  const [audit, setAudit] = useState([]);
  const [edits, setEdits] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const user = auth.me();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [starred, setStarred] = useState(false);
  const [togglingStar, setTogglingStar] = useState(false);

  useEffect(() => {
    if (!currentId) return;
    let cancelled = false;
    watchlistApi.ids()
      .then((r) => { if (!cancelled) setStarred((r.facility_ids || []).includes(currentId)); })
      .catch(() => { if (!cancelled) setStarred(false); });
    return () => { cancelled = true; };
  }, [currentId]);

  const toggleStar = async () => {
    if (togglingStar || !currentId) return;
    setTogglingStar(true);
    const next = !starred;
    setStarred(next);
    try {
      if (next) {
        await watchlistApi.star(currentId);
        toast.success("Added to your watchlist");
      } else {
        await watchlistApi.unstar(currentId);
        toast.success("Removed from watchlist");
      }
    } catch (e) {
      setStarred(!next);
      toast.error(getApiError(e, "Watchlist update failed"));
    } finally {
      setTogglingStar(false);
    }
  };

  useEffect(() => { setStack([facilityId]); }, [facilityId]);

  const openAlternative = (altId) => {
    if (!altId || altId === currentId) return;
    setStack((s) => (s.length >= 10 ? [...s.slice(-9), altId] : [...s, altId]));
    setEditing(false);
  };
  const goBack = () => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
    setEditing(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [f, a, ax, ex, hx] = await Promise.all([
        facilitiesApi.get(currentId),
        facilitiesApi.alternatives(currentId, 3),
        facilitiesApi.audit(currentId),
        facilitiesApi.edits(currentId).catch(() => ({ items: [] })),
        facilitiesApi.history(currentId).catch(() => ({ events: [] })),
      ]);
      setFacility(f);
      setAlts(a.alternatives || []);
      setAudit(ax.items || []);
      setEdits(ex.items || []);
      setHistory(hx.events || []);
    } catch (e) {
      toast.error("Failed to load facility");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentId) load();
    // eslint-disable-next-line
  }, [currentId]);

  const gpsHref = useMemo(() => facility?.maps_link || null, [facility]);

  const saveEdits = async (patch) => {
    setSaving(true);
    try {
      const res = await facilitiesApi.update(currentId, patch);
      const changed = Object.keys(res.changes || {}).length;
      toast.success(`${changed} field${changed !== 1 ? "s" : ""} updated`);
      setEditing(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(getApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (!facilityId) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/25 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div
        data-testid={TID.drawer}
        className="fixed inset-0 z-50 flex w-full flex-col border-l border-slate-200 bg-white shadow-2xl animate-slide-in-right md:inset-y-0 md:right-0 md:left-auto md:max-w-xl"
      >
        <div className="flex items-start justify-between border-b border-slate-200 p-5">
          {loading || !facility ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          ) : (
            <div className="min-w-0 flex-1">
              {stack.length > 1 && (
                <button
                  data-testid="drawer-back-button"
                  onClick={goBack}
                  className="mb-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-nmi-700 hover:bg-nmi-50"
                >
                  <ChevronLeft size={13} strokeWidth={2.5} /> Back
                </button>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill status={facility.status} size="lg" />
                {facility.missing_from_last_import && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">
                    <AlertTriangle size={11} /> Missing from last import
                  </span>
                )}
                {editing && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-nmi-300 bg-nmi-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-nmi-800">
                    <Pencil size={11} /> Editing
                  </span>
                )}
              </div>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-tight text-slate-900">
                {facility.name}
              </h2>
              <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono">{facility.facility_id}</span>
                <span>·</span>
                <span className="font-bold uppercase tracking-wider">{facility.category}</span>
              </div>
            </div>
          )}
          <div className="ml-3 flex shrink-0 items-center gap-1">
            {facility && (
              <button
                data-testid="drawer-star-toggle"
                onClick={toggleStar}
                disabled={togglingStar}
                className={`rounded-md p-1.5 transition-colors ${
                  starred
                    ? "text-amber-500 hover:bg-amber-50"
                    : "text-slate-400 hover:bg-slate-100 hover:text-amber-500"
                } disabled:opacity-50`}
                aria-label={starred ? "Remove from watchlist" : "Add to watchlist"}
                title={starred ? "Watching · click to unstar" : "Star to add to your watchlist"}
              >
                <Star size={18} strokeWidth={2} fill={starred ? "currentColor" : "none"} />
              </button>
            )}
            <button
              data-testid={TID.drawerClose}
              onClick={onClose}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              aria-label="Close"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {facility && (
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {!editing && isAdmin && (
              <div className="flex flex-wrap gap-2">
                {facility.status === "ACTIVE" ? (
                  <button
                    data-testid={TID.suspendTrigger}
                    onClick={() => setModal("suspend")}
                    className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700"
                  >
                    <Ban size={14} strokeWidth={2.5} /> Suspend
                  </button>
                ) : (
                  <button
                    data-testid={TID.reactivateTrigger}
                    onClick={() => setModal("reactivate")}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                  >
                    <CheckCircle2 size={14} strokeWidth={2.5} /> Reactivate
                  </button>
                )}
                <button
                  data-testid={TID.editTrigger}
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Pencil size={14} strokeWidth={2.5} /> Edit details
                </button>
                <button
                  data-testid={TID.whatsappShare(facility.facility_id)}
                  onClick={() => shareOnWhatsApp(facility)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-600"
                >
                  <MessageCircle size={14} strokeWidth={2.5} /> Share on WhatsApp
                </button>
                {gpsHref && (
                  <a
                    href={gpsHref} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <MapPin size={14} strokeWidth={2.5} /> Open in Maps
                    <ExternalLink size={12} />
                  </a>
                )}
                <button
                  data-testid={TID.reportIssueTrigger}
                  onClick={() => setModal("report")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                >
                  <AlertCircle size={14} strokeWidth={2.5} /> Report issue
                </button>
                {isSuperAdmin && (
                  <button
                    data-testid="facility-delete-btn"
                    onClick={() => setDeleteOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
                  >
                    <Trash2 size={14} strokeWidth={2.5} /> Delete
                  </button>
                )}
              </div>
            )}
            {!editing && !isAdmin && (
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid={TID.whatsappShare(facility.facility_id)}
                  onClick={() => shareOnWhatsApp(facility)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-600"
                >
                  <MessageCircle size={14} strokeWidth={2.5} /> Share on WhatsApp
                </button>
                {gpsHref && (
                  <a
                    href={gpsHref} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <MapPin size={14} strokeWidth={2.5} /> Open in Maps
                  </a>
                )}
                <button
                  data-testid={TID.reportIssueTrigger}
                  onClick={() => setModal("report")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
                >
                  <AlertCircle size={14} strokeWidth={2.5} /> Report issue
                </button>
              </div>
            )}

            {editing ? (
              <EditMode facility={facility} saving={saving} onCancel={() => setEditing(false)} onSave={saveEdits} />
            ) : (
              <ViewMode facility={facility} edits={edits} history={history} />
            )}

            {!editing && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-900">
                    Nearest active alternatives
                  </h3>
                  <span className="text-[10px] text-slate-500">{alts.length} results</span>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 divide-y divide-slate-200">
                  {alts.length === 0 && <div className="p-4 text-sm text-slate-500">No alternatives found.</div>}
                  {alts.map((a) => (
                    <div
                      key={a.facility_id}
                      data-testid={`alternative-row-${a.facility_id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openAlternative(a.facility_id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAlternative(a.facility_id); } }}
                      className="cursor-pointer p-3 transition-colors hover:bg-white focus:bg-white focus:outline-none"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-slate-900 hover:text-nmi-700">{a.name}</div>
                          <div className="text-[11px] text-slate-500">{a.town || "—"}, {a.region || "—"}</div>
                          {a.nmi_plans && (
                            <div className="mt-1 text-[11px] text-slate-700">
                              💳 <span className="font-medium">{a.nmi_plans}</span>
                            </div>
                          )}
                          {a.phones?.length > 0 && (
                            <div className="mt-0.5 font-mono text-[10px] text-slate-500">
                              📞 {a.phones.join(", ")}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {a.distance_km != null ? (
                            <div className="font-mono text-xs font-bold text-slate-900">{a.distance_km} km</div>
                          ) : (
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Same district</div>
                          )}
                          <div className="text-[10px] text-emerald-700 font-bold uppercase">Active</div>
                          <ChevronRight size={14} className="mt-1 ml-auto text-slate-400" />
                        </div>
                      </div>
                      <div className="mt-2 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                          data-testid={TID.whatsappShareAlt(a.facility_id)}
                          onClick={() => shareOnWhatsApp(a, { alternativeFor: facility.name })}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-600"
                        >
                          <MessageCircle size={11} strokeWidth={2.5} /> Share on WhatsApp
                        </button>
                        {a.maps_link && (
                          <a
                            href={a.maps_link}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            <MapPin size={11} /> Directions
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!editing && audit.length > 0 && (
              <div>
                <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wider text-slate-900">
                  Status change history
                </h3>
                <div className="rounded-md border border-slate-200 divide-y divide-slate-200">
                  {audit.slice(0, 6).map((a) => (
                    <div key={a.audit_id} className="flex items-start gap-3 p-3 text-sm">
                      <div className="mt-0.5">
                        {a.new_status === "SUSPENDED" ? <Ban size={14} className="text-red-600" /> : <CheckCircle2 size={14} className="text-emerald-600" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-slate-900">
                          {a.old_status} <ChevronRight size={11} className="inline" /> {a.new_status}
                          <span className="ml-2 rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">{a.channel}</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">{a.reason}</div>
                        <div className="text-[10px] text-slate-500 mt-1 font-mono">
                          {a.actor_name} · {a.timestamp?.slice(0, 16).replace("T", " ")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {modal && facility && modal !== "report" && (
          <StatusModal
            mode={modal}
            facility={facility}
            onClose={() => setModal(null)}
            onDone={async () => { setModal(null); await load(); onChanged?.(); }}
          />
        )}
        {modal === "report" && facility && (
          <ReportIssueModal
            facility={facility}
            onClose={() => setModal(null)}
            onSubmitted={() => setModal(null)}
          />
        )}
        {deleteOpen && facility && (
          <DeleteFacilityModal
            facility={facility}
            onClose={() => setDeleteOpen(false)}
            onDeleted={() => { setDeleteOpen(false); onClose(); onChanged?.(); }}
          />
        )}
      </div>
    </>
  );
};
